/** Import-facing task components (RFC 0325). */

import {
  createElement,
  Fragment,
  type ReactNode,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react'
import { createPortal } from 'react-dom'
import {
  ensureTaskElements,
  type TaskChipElement,
  type TaskElementRef,
  type TaskElementTask,
  type TaskHoverCardElement,
  type TaskListElement,
  type TaskPort,
  type TaskRowElement,
} from './taskElements'

ensureTaskElements()

export interface TaskRowProps {
  task?: TaskElementTask
  taskId?: string
  port?: TaskPort
  tickable?: boolean
  compact?: boolean
  nested?: boolean
  expandable?: boolean
  expanded?: boolean
  className?: string
  onChange?: (previous: TaskElementTask, task: TaskElementTask) => void
  onOpen?: (task: TaskElementTask, expanded: boolean) => void
}

export function TaskRow({
  task,
  taskId,
  port,
  tickable,
  compact,
  nested,
  expandable,
  expanded,
  className,
  onChange,
  onOpen,
}: TaskRowProps) {
  const ref = useRef<TaskRowElement>(null)
  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    element.port = port
    element.task = task
    element.taskId = taskId
    element.tickable = tickable
    element.compact = compact
    element.nested = nested
    element.expandable = expandable
    element.expanded = expanded
  }, [compact, expandable, expanded, nested, port, task, taskId, tickable])
  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    const changed = (event: Event) => {
      const detail = (event as CustomEvent).detail as {
        previous: TaskElementTask
        task: TaskElementTask
      }
      onChange?.(detail.previous, detail.task)
    }
    const opened = (event: Event) => {
      const detail = (event as CustomEvent).detail as {
        task: TaskElementTask
        expanded: boolean
      }
      onOpen?.(detail.task, detail.expanded)
    }
    element.addEventListener('mod-task-change', changed)
    element.addEventListener('mod-task-open', opened)
    return () => {
      element.removeEventListener('mod-task-change', changed)
      element.removeEventListener('mod-task-open', opened)
    }
  }, [onChange, onOpen])
  return createElement('mod-task-row', { ref, className })
}

export function TaskList({
  tasks,
  port,
  tickable,
  className,
}: {
  tasks: TaskElementTask[]
  port?: TaskPort
  tickable?: boolean
  className?: string
}) {
  const ref = useRef<TaskListElement>(null)
  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    element.tasks = tasks
    element.port = port
    element.tickable = tickable
  }, [port, tasks, tickable])
  return createElement('mod-task-list', { ref, className })
}

export function TaskChip({
  tasks,
  taskId,
  port,
  label,
  ariaLabel,
  popoverLabel,
  onOpenApp,
  includeCompleted,
  placement,
  popoverWidth,
  attention,
  refLabels,
  onOpenRef,
  children,
  className,
}: {
  tasks?: TaskElementTask[]
  taskId?: string
  port?: TaskPort
  label?: string
  ariaLabel?: string
  popoverLabel?: string
  onOpenApp?: () => void
  includeCompleted?: boolean
  placement?: 'bottom-end' | 'right-start'
  popoverWidth?: number
  attention?: boolean
  /** Names for ref targets, such as session titles, shown in a task's links. */
  refLabels?: Record<string, string>
  /** Open a document or session a task links to. */
  onOpenRef?: (ref: TaskElementRef) => void
  children?: ReactNode
  className?: string
}) {
  const ref = useRef<TaskChipElement>(null)
  const contentNode = useMemo(() => document.createElement('div'), [])
  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    element.tasks = tasks
    element.taskId = taskId
    element.port = port
    element.label = label
    element.accessibleLabel = ariaLabel
    element.popoverLabel = popoverLabel
    element.showOpenApp = Boolean(onOpenApp)
    element.includeCompleted = includeCompleted
    element.placement = placement
    element.popoverWidth = popoverWidth
    element.attention = attention
    element.refLabels = refLabels
    element.contentNode = children ? contentNode : null
  }, [
    ariaLabel,
    attention,
    refLabels,
    children,
    contentNode,
    includeCompleted,
    label,
    onOpenApp,
    placement,
    popoverLabel,
    popoverWidth,
    port,
    taskId,
    tasks,
  ])
  useLayoutEffect(() => {
    const element = ref.current
    if (!element || !onOpenApp) return
    element.addEventListener('mod-open-tasks', onOpenApp)
    return () => element.removeEventListener('mod-open-tasks', onOpenApp)
  }, [onOpenApp])
  useLayoutEffect(() => {
    const element = ref.current
    if (!element || !onOpenRef) return
    const opened = (event: Event) =>
      onOpenRef((event as CustomEvent<{ ref: TaskElementRef }>).detail.ref)
    element.addEventListener('mod-task-ref', opened)
    return () => element.removeEventListener('mod-task-ref', opened)
  }, [onOpenRef])
  return createElement(
    Fragment,
    null,
    createElement('mod-task-chip', { ref, className }),
    children ? createPortal(children, contentNode) : null,
  )
}

/**
 * A compact task affordance that opens its detail card on hover, focus, or
 * click.  `task` is deliberately a summary: the element asks its TaskPort for
 * descriptions and completion evidence only after the card opens.
 */
export function TaskHoverCard({
  task,
  taskId,
  contextTasks,
  port,
  className,
}: {
  task?: TaskElementTask
  taskId?: string
  contextTasks?: TaskElementTask[]
  port?: TaskPort
  className?: string
}) {
  const ref = useRef<TaskHoverCardElement>(null)
  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    element.task = task
    element.taskId = taskId
    element.contextTasks = contextTasks
    element.port = port
  }, [contextTasks, port, task, taskId])
  return createElement('mod-task-hover-card', { ref, className })
}

export type {
  TaskElementRef,
  TaskElementTask,
  TaskPort,
} from './taskElements'
